import dayjs, { Dayjs } from "dayjs";
import apiService, {
  GetRefundsRequest,
  parseGetRefundsResponse,
} from "../../services/api";
import { REFUND_STATUS_CODE } from "./editRefundDepositBreakdown";
import {
  pickRefundRecordScalar,
  REFUND_RIDE_END_DATE_KEYS,
  REFUND_RIDE_START_DATE_KEYS,
} from "./refundDateFormat";

type RefundReasonOption = {
  codevalue?: string | number;
  codedisplayname?: string;
  codename?: string;
  codeName?: string;
};

type RefundTypeOption = {
  codevalue?: string | number;
  codedisplayname?: string;
};

const getScalar = (item: Record<string, unknown>, keys: string[]): unknown => {
  for (const k of keys) {
    if (k in item && item[k] != null && item[k] !== "") return item[k];
  }
  return undefined;
};

function getRefundReasonCodeName(opt: RefundReasonOption | undefined): string {
  if (!opt) return "";
  return String(
    opt.codename ?? opt.codeName ?? opt.codedisplayname ?? "",
  ).trim();
}

function findRefundReasonOptionById(
  refundreasonid: number | null,
  options: RefundReasonOption[],
): RefundReasonOption | undefined {
  if (refundreasonid == null || refundreasonid <= 0) return undefined;
  const idStr = String(refundreasonid);
  return options.find((o) => String(o.codevalue ?? "").trim() === idStr);
}

/** Same as refund details "Refunded Reason / Remark". */
export function resolveRefundedReasonDisplay(
  refundreasonid: number | null,
  updatereason: string | null,
  options: RefundReasonOption[],
): string {
  const matched = findRefundReasonOptionById(refundreasonid, options);
  const codename = getRefundReasonCodeName(matched);
  const remark = updatereason?.trim() ?? "";

  if (codename && remark) {
    if (remark.toLowerCase() === codename.toLowerCase()) return codename;
    return `${codename}: ${remark}`;
  }
  if (codename) return codename;
  if (remark) return remark;
  return "";
}

function formatPaymentIdForExport(r: Record<string, unknown>): string {
  const tid = String(getScalar(r, ["transactionid", "transactionId"]) ?? "");
  if (tid.toLowerCase().includes("cash")) return "Cash";
  return tid;
}

function resolveRefundExportAmount(r: Record<string, unknown>): number {
  let val = 0;
  const direct = getScalar(r, ["amount", "Amount"]);
  if (direct != null && direct !== "") {
    const n = Number(direct);
    if (Number.isFinite(n)) val = n;
  } else {
    const statusCode = Number(
      getScalar(r, [
        "paymentstatus",
        "paymentStatus",
        "refundstatus",
        "refundStatus",
      ]) ?? 0,
    );
    if (statusCode === REFUND_STATUS_CODE.PROCESSED) {
      val = Number(getScalar(r, ["proposedamount", "proposedAmount"]) ?? 0);
    } else {
      val = Number(getScalar(r, ["refundableamount", "refundableAmount"]) ?? 0);
    }
  }
  return Math.round(val * 100);
}

function resolveProposedRefundableAmountRemark(
  r: Record<string, unknown>,
): string {
  return String(
    getScalar(r, ["refunddescription", "refundDescription"]) ?? "",
  ).trim();
}

function resolveRefundedReasonRemarkForExport(
  r: Record<string, unknown>,
  refundReasonOptions: RefundReasonOption[],
): string {
  const statusCode = Number(
    getScalar(r, ["paymentstatus", "paymentStatus"]) ?? 0,
  );
  if (statusCode !== REFUND_STATUS_CODE.PROCESSED) return "";

  const refundreasonidRaw = getScalar(r, [
    "refundreasonid",
    "refundReasonId",
    "reasonrefundid",
    "reasonRefundId",
  ]);
  let id: number | null = null;
  if (refundreasonidRaw != null && refundreasonidRaw !== "") {
    const n = Number(refundreasonidRaw);
    if (Number.isFinite(n) && n > 0) id = n;
  }

  const updatereasonRaw = getScalar(r, [
    "updatereason",
    "updateReason",
    "remark",
    "Remark",
  ]);
  const updatereason =
    updatereasonRaw == null || String(updatereasonRaw).trim() === ""
      ? null
      : String(updatereasonRaw).trim();

  return resolveRefundedReasonDisplay(id, updatereason, refundReasonOptions);
}

/** Speed column: `refundtype` 0 → `-`; else RefundType master `codedisplayname`. */
export function resolveRefundTypeSpeedForExport(
  r: Record<string, unknown>,
  refundTypeOptions: RefundTypeOption[],
): string {
  const raw = getScalar(r, ["refundtype", "refundType"]);
  if (raw == null || raw === "") return "Normal";
  const n = Number(raw);
  if (!Number.isFinite(n) || n === 0) return "Normal";
  const idStr = String(n);
  const matched = refundTypeOptions.find(
    (o) => String(o.codevalue ?? "").trim() === idStr,
  );
  const label = String(matched?.codedisplayname ?? "").trim();
  return label || "Normal";
}

function isDateInRange(
  iso: string,
  from: Dayjs | null,
  to: Dayjs | null,
): boolean {
  const d = dayjs(iso);
  if (!d.isValid()) return false;
  if (from && d.isBefore(from.startOf("day"))) return false;
  if (to && d.isAfter(to.endOf("day"))) return false;
  return true;
}

/** Client-side filter by ride start and/or ride completion timestamps. */
export function filterRefundItemsByRideDateRange(
  items: unknown[],
  from: Dayjs | null,
  to: Dayjs | null,
): unknown[] {
  if (!from && !to) return items;
  return items.filter((item) => {
    const r =
      item && typeof item === "object" ? (item as Record<string, unknown>) : {};
    const candidates = [
      pickRefundRecordScalar(r, REFUND_RIDE_START_DATE_KEYS),
      pickRefundRecordScalar(r, REFUND_RIDE_END_DATE_KEYS),
    ];
    return candidates.some((d) => {
      if (d == null || String(d).trim() === "") return false;
      return isDateInRange(String(d), from, to);
    });
  });
}

export async function fetchRefundsForExport(
  body: GetRefundsRequest,
  rideFrom: Dayjs | null,
  rideTo: Dayjs | null,
): Promise<unknown[]> {
  const hasRideRange = Boolean(rideFrom || rideTo);
  const response = await apiService.getRefunds({
    ...body,
    pageNumber: 1,
    pageSize: 10000,
    /** Broader fetch when ride range is set; narrowed client-side below. */
    ridestartdate: hasRideRange ? null : body.ridestartdate,
    ridecompletiondate: hasRideRange ? null : body.ridecompletiondate,
  });
  const { items } = parseGetRefundsResponse(response);
  return filterRefundItemsByRideDateRange(items, rideFrom, rideTo);
}

export function transformRefundsForCsvExport(
  items: unknown[],
  refundReasonOptions: RefundReasonOption[] = [],
  refundTypeOptions: RefundTypeOption[] = [],
  isBulkRefund: boolean = false,
): Record<string, string | number>[] {
  const cashMethodCodes = new Set([5, 6]);
  const filteredItems = isBulkRefund
    ? items.filter((item) => {
      const r = item && typeof item === "object" ? (item as Record<string, unknown>) : {};
      const pm = Number(r.paymentmethod);
      const isCash = Number.isFinite(pm) && cashMethodCodes.has(pm);
      return Number(r.paymentstatus) === 2 && !isCash;
    })
    : items;

  return filteredItems.map((item) => {
    const r =
      item && typeof item === "object" ? (item as Record<string, unknown>) : {};

    return {
      "Payment Id": formatPaymentIdForExport(r),
      Amount: resolveRefundExportAmount(r),
      Speed: resolveRefundTypeSpeedForExport(r, refundTypeOptions),
      "Proposed Refundable Remark": resolveProposedRefundableAmountRemark(r),
      "Refunded Reason/Remark": resolveRefundedReasonRemarkForExport(
        r,
        refundReasonOptions,
      ),
    };
  });
}

export function buildRefundsExportFilename(
  exportFrom: Dayjs | null,
  exportTo: Dayjs | null,
): string {
  const from = exportFrom?.format("YYYY-MM-DD") ?? "all";
  const to = exportTo?.format("YYYY-MM-DD") ?? "all";
  return `refunds-export_${from}_to_${to}.csv`;
}
